import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Button,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';

import { Operation } from '../../../../../networkRequest/operationsService';
import SearchableAgencyOfficialDropdown, { SearchableAgencyOfficialDropdownOption } from '@components/SearchableAgencyOfficialDropdown';
import ParticipantsTable from './ParticipantsTable';
import { useUser } from '@hooks/useUser';

interface ParticipantsSectionProps {
  operation: Operation;
  onOperationUpdate: (op: Operation) => void;
  onAddTeam: () => void;
  setAddParticipantDialogOpen: (boolean) => void;
  setSelectedAgencyOfficial: (SearchableAgencyOfficialDropdownOption) => void;
  selectedAgencyOfficial: SearchableAgencyOfficialDropdownOption | null;
}

export default function ParticipantsSection({
  operation,
  onOperationUpdate,
  setAddParticipantDialogOpen,
  setSelectedAgencyOfficial,
  onAddTeam,
  selectedAgencyOfficial
}: ParticipantsSectionProps) {
  const { user } = useUser();
  const [loading, /*setLoading*/] = useState(false);

  // All active participants
  const participants = operation.operation_agency_officials;

  // Count participants per team
  const teamCounts: { [teamId: string]: number } = useMemo(() => {
    const counts: { [teamId: string]: number } = {};
    participants.forEach((p) => {
      if (p.operation_team_members && p.operation_team_members.length > 0) {
        p.operation_team_members.forEach((tm) => {
          if (tm.operation_team_id) {
            counts[tm.operation_team_id] = (counts[tm.operation_team_id] || 0) + 1;
          }
        });
      }
    });
    return counts;
  }, [participants]);

  const handleAddParticipant = () => {
    setAddParticipantDialogOpen(true)
  };

  return (
    <Box sx={{ mt: 4 }}>
      {/* Header: Participants icon and label */}
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
          mb: 2
        }}>
        <PersonIcon sx={{ mr: 1 }} />
        <Typography variant="h5">Participants</Typography>
      </Stack>

      {/* Main content: Teams box and table/search area side by side */}
      <Stack direction="row" spacing={2} sx={{
        alignItems: "flex-start"
      }}>
        {/* Teams box */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: 'blueGrey.50',
            borderRadius: 1,
            p: 2,
            minWidth: 180,
            maxWidth: 220,
            width: 220,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            mr: 2
          }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              mb: 1,
              width: '100%',
              justifyContent: 'space-between'
            }}>
            <Stack direction="row" spacing={1} sx={{
              alignItems: "center"
            }}>
              <GroupIcon fontSize="small" />
              <Typography variant="subtitle1" sx={{
                fontWeight: 600
              }}>
                Teams
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<Add />}
              onClick={() => {
                onAddTeam();
              }}
              sx={{ width: '84px', minWidth: '84px' }}
            >
              Team
            </Button>
          </Stack>
          {/* All participants count (white background) */}
          <Box
            sx={{
              background: '#fff',
              borderRadius: 1,
              px: 2,
              py: 1,
              mb: 1,
              width: '100%',
              display: 'flex',
              alignItems: 'center'
            }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
              {participants.length}
            </Typography>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              All
            </Typography>
          </Box>
          {/* Team counts */}
          {operation.operation_teams?.map((team) => (
            <Box
              key={team.id}
              sx={{
                borderRadius: 1,
                px: 2,
                py: 1,
                mb: 0.5,
                width: '100%',
                display: 'flex',
                alignItems: 'center'
              }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
                {teamCounts[team.id || ''] || 0}
              </Typography>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                {team.name}
              </Typography>
            </Box>
          ))}
        </Paper>

        {/* Main Participants area */}
        <Box sx={{ flex: 1 }}>
          {/* Add Participant row - now full width above the table */}
          <Box sx={{ width: '100%', mb: 2 }}>
            <SearchableAgencyOfficialDropdown
              label="Participant"
              accessToken={user?.accessToken || ''}
              value={null}
              onSelect={(official) => setSelectedAgencyOfficial(official)}
              onError={error => { throw new Error(error) }}
              hideAssignmentField={true}
            />
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              disabled={!selectedAgencyOfficial || loading}
              onClick={handleAddParticipant}
            >
              Add
            </Button>
          </Box>

          <Paper elevation={0} sx={{ mt: 2, p: 0 }}>
            <ParticipantsTable
              participants={participants}
              teams={operation.operation_teams}
              onOperationUpdate={onOperationUpdate}
            />
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
}


